<template>
  <div class="upload-section">
    <div class="upload-header">
      <q-icon :name="headerIcon" size="24px" class="header-icon" />
      <h6 class="upload-title">{{ titulo }}</h6>
    </div>

    <div class="upload-content">
      <div
        class="upload-area"
        :class="{ 'file-selected': file, 'drag-over': isDragOver }"
        @dragover.prevent="$emit('drag-over')"
        @dragleave.prevent="$emit('drag-leave')"
        @drop.prevent="$emit('drop-file', $event)"
      >
        <q-btn
          v-if="showInstructions"
          :icon="instructionsIcon"
          :label="instructionsLabel"
          no-caps
          rounded
          unelevated
          class="instructions-btn"
          @click.stop="$emit('instructions')"
        />

        <div class="upload-zone">
          <div class="upload-icon-container">
            <q-icon name="cloud_upload" size="48px" class="upload-icon" />
          </div>
          <div class="upload-text-container">
            <p class="upload-main-text" v-if="!file">
              {{ uploadText }}
            </p>
            <p class="upload-main-text selected" v-else>
              <q-icon name="description" class="q-mr-xs" />{{ file.name }}
            </p>
            <div class="upload-button">
              <q-btn
                :color="file ? 'positive' : 'primary'"
                :icon="file ? 'check_circle' : 'folder_open'"
                :label="file ? selectedFileLabel : selectFileLabel"
                no-caps
                rounded
                class="upload-btn"
                @click="triggerFileInput"
              />
              <input
                ref="fileInput"
                type="file"
                :accept="accept"
                @change="$emit('file-selected', $event)"
                style="display: none"
              />
            </div>
          </div>
        </div>

        <div class="file-info" v-if="file">
          <div class="file-details">
            <q-chip color="primary" text-color="white" icon="info">
              Tamanho: {{ formatFileSize(file.size) }}
            </q-chip>
            <q-chip color="positive" text-color="white" icon="event">
              Tipo: {{ file.type || fileTypeFallback }}
            </q-chip>
          </div>
        </div>
      </div>

      <div class="upload-actions">
        <div class="action-buttons">
          <q-btn
            color="positive"
            :label="submitLabel"
            no-caps
            :icon="submitIcon"
            size="md"
            :disable="submitDisabled"
            @click="$emit('submit')"
            class="send-button"
          />
          <slot name="extra-actions" />
        </div>
      </div>

      <slot />
    </div>
  </div>
</template>

<script>
import { ref } from "vue";

export default {
  props: {
    titulo: {
      type: String,
      required: true,
    },
    file: {
      type: Object,
      default: null,
    },
    isDragOver: {
      type: Boolean,
      default: false,
    },
    submitDisabled: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: "Enviar Planilha",
    },
    submitIcon: {
      type: String,
      default: "send",
    },
    headerIcon: {
      type: String,
      default: "upload_file",
    },
    showInstructions: {
      type: Boolean,
      default: true,
    },
    instructionsIcon: {
      type: String,
      default: "info_outline",
    },
    instructionsLabel: {
      type: String,
      default: "Ver instruções",
    },
    uploadText: {
      type: String,
      default: "Arraste e solte sua planilha aqui ou",
    },
    selectFileLabel: {
      type: String,
      default: "Selecionar Arquivo",
    },
    selectedFileLabel: {
      type: String,
      default: "Arquivo Selecionado",
    },
    accept: {
      type: String,
      default: ".xlsx,.xls",
    },
    fileTypeFallback: {
      type: String,
      default: "Excel",
    },
  },
  emits: [
    "drag-over",
    "drag-leave",
    "drop-file",
    "file-selected",
    "instructions",
    "submit",
  ],
  setup() {
    const fileInput = ref(null);

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    return { fileInput, formatFileSize, triggerFileInput };
  },
};
</script>

<style scoped>
.upload-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.upload-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
}

.header-icon {
  color: #1976d2;
}

.upload-title {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1rem;
}

.upload-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-area {
  background: white;
  border: 2px dashed #e0e4e7;
  border-radius: 12px;
  padding: 56px 32px 32px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.instructions-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #173462;
  color: white;
  font-weight: 700;
  min-height: 36px;
  padding: 0 18px;
  box-shadow: 0 2px 6px rgba(23, 52, 98, 0.25);
  z-index: 1;
}

.upload-area:hover {
  border-color: #1976d2;
  background: #f8fafe;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.1);
}

.upload-area.file-selected {
  border-color: #4caf50;
  background: #f1f8e9;
}

.upload-area.drag-over {
  border-color: #1976d2;
  background: #e3f2fd;
  transform: scale(1.02);
}

.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-icon-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.upload-icon {
  color: white;
}

.upload-text-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-main-text {
  margin: 0;
  color: #546e7a;
  font-size: 1rem;
  font-weight: 500;
}

.upload-main-text.selected {
  color: #2e7d32;
  font-weight: 600;
}

.upload-button {
  cursor: pointer;
}

.upload-btn {
  min-width: 180px;
  height: 44px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.upload-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.file-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e4e7;
}

.file-details {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.upload-actions {
  display: flex;
  justify-content: center;
}

.action-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.send-button,
:slotted(.template-button) {
  min-width: 160px;
  height: 44px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.send-button:hover,
:slotted(.template-button:hover) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

@media (max-width: 600px) {
  .upload-section {
    padding: 16px;
    margin: 12px 0;
  }

  .upload-area {
    padding: 64px 16px 20px;
  }

  .instructions-btn {
    left: 16px;
    right: 16px;
  }

  .upload-icon-container {
    width: 64px;
    height: 64px;
  }

  .upload-icon {
    font-size: 32px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .send-button,
  :slotted(.template-button) {
    width: 100%;
  }

  .file-details {
    flex-direction: column;
    align-items: center;
  }
}
</style>
